import {autoUpdate, computePosition, flip, offset, shift, size} from '@floating-ui/dom';
import {BREAKPOINTS} from '../../modules/breakpoint.js';
import {LAYOUT_VARS, getCssVariableInPixels} from '../../utils/cssVariables.js';
import {defineCustomElement} from '../../utils/defineCustomElement.js';
import {SelectTypingEffect} from './typing.js';

/** Gap the open panel keeps from the viewport edges and the fixed layout bars. */
const PANEL_VIEWPORT_PADDING = 8;

/** A desktop combobox backed by a server-rendered native select. */
export class CustomSelectInputGroup extends HTMLElement {
    /** @type {HTMLSelectElement} */
    select;
    /** @type {HTMLButtonElement} */
    trigger;
    /** @type {HTMLUListElement} */
    panel;
    /** @type {HTMLElement} */
    displayValue;
    /** @type {HTMLTemplateElement} */
    optionTemplate;
    /** @type {AbortController|null} */
    controller = null;
    /** @type {MutationObserver} */
    observer;
    /** @type {MediaQueryList} */
    desktop;
    /** @type {SelectTypingEffect} */
    typing;
    /** @type {{element: HTMLElement, option: HTMLOptionElement}[]} */
    rows = [];
    /** @type {(() => void)|null} */
    cleanupPosition = null;
    /** @type {AbortController|null} */
    openController = null;
    activeIndex = -1;
    search = '';
    searchTime = 0;
    positionVersion = 0;

    connectedCallback() {
        // A fragment can connect before its children have been parsed.
        queueMicrotask(() => {
            if (this.isConnected && !this.controller) this.initialize();
        });
    }

    initialize() {
        this.select = this.querySelector('[data-native-select]');
        if (!this.select) throw new Error('Custom select requires a native select.');
        if (this.select.multiple || this.hasAttribute('data-force-native')) return;
        this.trigger = this.querySelector('[data-dropdown-trigger]');
        this.panel = this.querySelector('[data-dropdown-panel]');
        this.displayValue = this.querySelector('[data-display-value]');
        this.optionTemplate = this.querySelector('[data-option-template]');
        if (!this.trigger || !this.panel || !this.displayValue || !this.optionTemplate) {
            throw new Error('Custom select markup is incomplete.');
        }
        this.controller = new AbortController();
        const {signal} = this.controller;
        this.desktop = matchMedia(`(min-width: ${BREAKPOINTS.lg}px) and (hover: hover) and (pointer: fine)`);
        this.typing = new SelectTypingEffect(this, this.displayValue);
        this.buildOptions();
        this.desktop.addEventListener('change', () => this.updateMode(), {signal});
        this.select.addEventListener('change', () => this.sync(), {signal});
        this.select.addEventListener('input', () => this.sync(), {signal});
        this.select.addEventListener('invalid', event => this.showValidation(event), {signal});
        this.select.form?.addEventListener('reset', () => queueMicrotask(() => this.sync()), {signal});
        window.addEventListener('pageshow', () => this.sync(), {signal});
        this.trigger.addEventListener('click', () => this.isOpen ? this.close() : this.open(), {signal});
        this.trigger.addEventListener('keydown', event => this.onKeydown(event), {signal});
        this.trigger.addEventListener('focus', () => this.syncTyping(), {signal});
        this.trigger.addEventListener('blur', () => {
            if (this.isOpen) this.commit();
            this.syncTyping();
        }, {signal});
        for (const label of this.select.labels) {
            label.addEventListener('click', event => {
                if (!this.enhanced) return;
                event.preventDefault();
                this.trigger.focus();
            }, {signal});
        }
        this.panel.addEventListener('pointerdown', event => event.preventDefault(), {signal});
        this.panel.addEventListener('click', event => {
            const row = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
            const index = this.rows.findIndex(({element}) => element === row);
            if (!this.isSelectable(index)) return;
            this.setActive(index);
            this.commit();
        }, {signal});
        this.observer = new MutationObserver(() => {
            this.close();
            this.buildOptions();
            this.updateMode();
        });
        this.observer.observe(this.select, {
            subtree: true, childList: true, characterData: true, attributes: true,
            attributeFilter: ['disabled', 'required', 'selected', 'label', 'value', 'hidden', 'aria-invalid', 'aria-describedby', 'aria-label', 'aria-labelledby'],
        });
        const fieldset = this.closest('fieldset');
        if (fieldset) this.observer.observe(fieldset, {attributes: true, attributeFilter: ['disabled']});
        this.updateMode();
    }

    disconnectedCallback() {
        this.close();
        this.removeAttribute('data-enhanced');
        if (this.trigger) this.trigger.hidden = true;
        this.select?.removeAttribute('aria-hidden');
        this.select?.removeAttribute('tabindex');
        this.controller?.abort();
        this.controller = null;
        this.observer?.disconnect();
        this.typing?.destroy();
    }

    /** @returns {boolean} */
    get enhanced() {
        return this.hasAttribute('data-enhanced');
    }

    /** @returns {boolean} */
    get isOpen() {
        return this.openController !== null;
    }

    updateMode() {
        const enhanced = this.desktop.matches && !this.closest('dialog');
        const hadFocus = this.contains(document.activeElement);
        this.close();
        this.toggleAttribute('data-enhanced', enhanced);
        this.trigger.hidden = !enhanced;
        if (enhanced) {
            this.select.tabIndex = -1;
            this.select.setAttribute('aria-hidden', 'true');
        } else {
            this.select.removeAttribute('tabindex');
            this.select.removeAttribute('aria-hidden');
        }
        this.sync();
        if (hadFocus) (enhanced ? this.trigger : this.select).focus({preventScroll: true});
    }

    buildOptions() {
        this.rows = [];
        this.panel.replaceChildren();
        for (const child of this.select.children) {
            if (child instanceof HTMLOptGroupElement) {
                const group = document.createElement('li');
                group.setAttribute('role', 'group');
                group.setAttribute('aria-label', child.label);
                group.className = 'customSelect__group';
                const label = document.createElement('div');
                label.className = 'customSelect__group-label';
                label.setAttribute('aria-hidden', 'true');
                this.setContent(label, child.dataset.labelHtml, child.label);
                const list = document.createElement('ul');
                list.setAttribute('role', 'presentation');
                group.append(label, list);
                this.panel.append(group);
                for (const option of child.children) this.appendOption(option, list);
            } else if (child instanceof HTMLHRElement) {
                const divider = document.createElement('li');
                divider.setAttribute('role', 'separator');
                divider.className = 'customSelect__divider';
                this.panel.append(divider);
            } else {
                this.appendOption(child, this.panel);
            }
        }
    }

    /**
     * @param {Element} option
     * @param {HTMLElement} container
     */
    appendOption(option, container) {
        if (!(option instanceof HTMLOptionElement) || option.hidden || option.value === '') return;
        const element = this.optionTemplate.content.firstElementChild.cloneNode(true);
        if (!(element instanceof HTMLElement)) throw new Error('Custom select requires an option template.');
        element.id = `${this.select.id}-option-${this.rows.length}`;
        const disabled = option.disabled || (option.parentElement instanceof HTMLOptGroupElement && option.parentElement.disabled);
        element.setAttribute('aria-disabled', String(disabled));
        this.setContent(element.querySelector('[data-option-label]'), option.dataset.optionHtml, option.label);
        this.rows.push({element, option});
        container.append(element);
    }

    /**
     * @param {HTMLElement} element
     * @param {string|undefined} html HTML from an escaped Blade slot attribute.
     * @param {string} text
     */
    setContent(element, html, text) {
        if (html === undefined) {
            element.textContent = text;
            return;
        }
        const template = document.createElement('template');
        template.innerHTML = html;
        element.replaceChildren(template.content.cloneNode(true));
    }

    sync() {
        this.close();
        this.trigger.disabled = this.select.matches(':disabled');
        const message = this.querySelector('[data-validation-message]');
        const showError = !message.hidden && !this.select.validity.valid;
        message.hidden = !showError;
        if (showError) message.textContent = this.select.validationMessage;
        for (const attribute of ['aria-label', 'aria-labelledby', 'aria-describedby', 'aria-invalid', 'aria-required']) {
            let value = attribute === 'aria-required' ? String(this.select.required) : this.select.getAttribute(attribute);
            if (showError && attribute === 'aria-invalid') value = 'true';
            if (showError && attribute === 'aria-describedby') value = [value, message.id].filter(Boolean).join(' ');
            if (value === null) this.trigger.removeAttribute(attribute);
            else this.trigger.setAttribute(attribute, value);
        }
        for (const {element, option} of this.rows) {
            element.setAttribute('aria-selected', String(option.selected));
        }
        this.syncTyping();
        const value = this.select.options[this.select.selectedIndex]?.label ?? this.dataset.placeholder;
        if (!this.typing.running) this.displayValue.textContent = value;
        this.querySelector('[data-accessible-value]').textContent = value;
    }

    syncTyping() {
        this.typing.update(this.enhanced && !this.select.matches(':disabled') && !this.select.value
            && !this.isOpen && document.activeElement !== this.trigger);
    }

    /** @param {Event} event */
    showValidation(event) {
        if (!this.enhanced) return;
        event.preventDefault();
        this.close();
        this.trigger.setAttribute('aria-invalid', 'true');
        const message = this.querySelector('[data-validation-message]');
        message.textContent = this.select.validationMessage;
        message.hidden = false;
        this.trigger.setAttribute('aria-describedby', [this.select.getAttribute('aria-describedby'), message.id].filter(Boolean).join(' '));
        this.trigger.focus();
    }

    open() {
        if (!this.enhanced || this.isOpen || this.select.matches(':disabled') || !this.rows.some((_, index) => this.isSelectable(index))) return;
        const active = this.rows.findIndex(({option}) => option.selected);
        const designated = this.rows.findIndex(({option}) => option.dataset.focusOnOpen === 'true');
        this.activeIndex = this.isSelectable(active) ? active : this.isSelectable(designated) ? designated : this.rows.findIndex((_, index) => this.isSelectable(index));
        this.openController = new AbortController();
        const {signal} = this.openController;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.panel.style.maxHeight = '';
        this.searchTime = 0;
        this.syncTyping();
        // The landing section has paint containment. The panel must live outside it.
        document.body.append(this.panel);
        const styles = getComputedStyle(this.trigger);
        this.panel.dir = styles.direction;
        this.panel.style.colorScheme = styles.colorScheme;
        for (const token of ['--control-bg-panel', '--control-text', '--control-border', '--control-border-subtle', '--control-focus', '--control-bg-hover', '--control-bg-subtle', '--control-bg-muted']) {
            this.panel.style.setProperty(token, styles.getPropertyValue(token));
        }
        this.panel.hidden = false;
        this.panel.style.visibility = 'hidden';
        this.panel.style.width = `${this.trigger.getBoundingClientRect().width}px`;
        this.setActive(this.activeIndex);
        this.cleanupPosition = autoUpdate(this.trigger, this.panel, () => this.position());
        document.addEventListener('pointerdown', event => {
            if (this.contains(event.target) || this.panel.contains(event.target)) return;
            this.commit();
        }, {signal});
    }

    async position() {
        this.positionVersion += 1;
        const version = this.positionVersion;
        // The fixed header and the sticky bottom bar are opaque to the panel: keep it out from under them.
        const headerOffset = getCssVariableInPixels(LAYOUT_VARS.headerOffset);
        const bottomOffset = getCssVariableInPixels(LAYOUT_VARS.bottomOffset);
        const trigger = this.trigger.getBoundingClientRect();
        // A panel anchored to a trigger that scrolled under a bar or out of the viewport can only land on that bar.
        if (trigger.bottom < headerOffset || trigger.top > window.innerHeight - bottomOffset) {
            this.close();
            return;
        }
        const padding = {
            top: PANEL_VIEWPORT_PADDING + headerOffset,
            right: PANEL_VIEWPORT_PADDING,
            bottom: PANEL_VIEWPORT_PADDING + bottomOffset,
            left: PANEL_VIEWPORT_PADDING,
        };
        const {x, y, placement} = await computePosition(this.trigger, this.panel, {
            placement: 'bottom-start', strategy: 'fixed',
            middleware: [offset(-1), flip({padding}), shift({padding}), size({
                padding,
                apply: ({availableHeight, rects, elements}) => {
                    elements.floating.style.width = `${rects.reference.width}px`;
                    elements.floating.style.maxHeight = `${Math.max(0, Math.min(300, availableHeight))}px`;
                },
            })],
        });
        if (!this.isOpen || version !== this.positionVersion) return;
        Object.assign(this.panel.style, {left: `${x}px`, top: `${y}px`, visibility: 'visible'});
        const side = placement.split('-')[0];
        this.panel.dataset.side = side;
        this.dataset.side = side;
        this.scrollActiveIntoView();
    }

    close() {
        if (!this.isOpen) return;
        this.openController.abort();
        this.openController = null;
        this.cleanupPosition?.();
        this.cleanupPosition = null;
        this.positionVersion += 1;
        this.panel.hidden = true;
        this.append(this.panel);
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.removeAttribute('aria-activedescendant');
        delete this.dataset.side;
        this.search = '';
        this.syncTyping();
    }

    commit() {
        const row = this.rows[this.activeIndex];
        const changed = this.isSelectable(this.activeIndex) && this.select.value !== row.option.value;
        if (changed) this.select.value = row.option.value;
        this.close();
        this.sync();
        if (changed) {
            this.select.dispatchEvent(new Event('input', {bubbles: true}));
            this.select.dispatchEvent(new Event('change', {bubbles: true}));
        }
    }

    /**
     * @param {number} index
     * @returns {boolean}
     */
    isSelectable(index) {
        return this.rows[index]?.element.getAttribute('aria-disabled') === 'false';
    }

    /** @param {number} index */
    setActive(index) {
        if (!this.isSelectable(index)) return;
        this.activeIndex = index;
        this.rows.forEach(({element}, position) => element.toggleAttribute('data-active', position === index));
        this.trigger.setAttribute('aria-activedescendant', this.rows[index].element.id);
        this.scrollActiveIntoView();
    }

    scrollActiveIntoView() {
        const active = this.rows[this.activeIndex]?.element;
        if (!active) return;
        const row = active.getBoundingClientRect();
        const panel = this.panel.getBoundingClientRect();
        if (row.top < panel.top) this.panel.scrollTop -= panel.top - row.top;
        else if (row.bottom > panel.bottom) this.panel.scrollTop += row.bottom - panel.bottom;
    }

    /** @param {number} direction */
    move(direction) {
        let next = this.activeIndex + direction;
        while (next >= 0 && next < this.rows.length) {
            if (this.isSelectable(next)) {
                this.setActive(next);
                return;
            }
            next += direction;
        }
    }

    /** @param {KeyboardEvent} event */
    onKeydown(event) {
        if (event.ctrlKey || event.metaKey || event.isComposing) return;
        if (event.key === 'Tab') {
            if (this.isOpen) this.commit();
            return;
        }
        if (event.key === 'Escape') {
            if (!this.isOpen) return;
            event.preventDefault();
            this.close();
            return;
        }
        if (event.altKey && event.key === 'ArrowUp') {
            if (this.isOpen) this.commit();
            event.preventDefault();
            return;
        }
        const commands = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '];
        if (!commands.includes(event.key) && (event.key.length !== 1 || event.altKey)) return;
        event.preventDefault();
        const wasOpen = this.isOpen;
        this.open();
        if (!this.isOpen) return;
        if (event.key === 'Home') this.setActive(this.rows.findIndex((_, index) => this.isSelectable(index)));
        else if (event.key === 'End') this.setActive(this.rows.findLastIndex((_, index) => this.isSelectable(index)));
        else if (event.key === 'ArrowDown' && wasOpen && !event.altKey) this.move(1);
        else if (event.key === 'ArrowUp' && wasOpen) this.move(-1);
        else if (event.key === 'Enter' || event.key === ' ') {
            if (wasOpen) this.commit();
        } else if (event.key.length === 1) this.typeahead(event.key, wasOpen);
    }

    /**
     * @param {string} key
     * @param {boolean} wasOpen
     */
    typeahead(key, wasOpen) {
        const now = Date.now();
        this.search = now - this.searchTime < 700 ? this.search + key.toLocaleLowerCase() : key.toLocaleLowerCase();
        this.searchTime = now;
        const repeated = [...this.search].every(character => character === this.search[0]);
        const query = repeated ? this.search[0] : this.search;
        const start = wasOpen ? this.activeIndex + (repeated ? 1 : 0) : 0;
        for (let offset = 0; offset < this.rows.length; offset++) {
            const index = (start + offset + this.rows.length) % this.rows.length;
            if (this.isSelectable(index) && this.rows[index].option.label.trim().toLocaleLowerCase().startsWith(query)) {
                this.setActive(index);
                return;
            }
        }
    }
}

defineCustomElement('custom-select-input-group', CustomSelectInputGroup);
